/**
 * Выпадающее меню.
 *
 * Правила app-kit.md §4: `aria-expanded` на кнопке, Escape закрывает, стрелки
 * двигают выбор, клик мимо закрывает, фокус возвращается на кнопку. Меню — не
 * `<select>`: в переключателе контекста строка несёт две-три подписи, и родной
 * список их не покажет.
 *
 * Клавиатура обязательна: это самый частый контрол продукта, а работать с ним
 * будет человек, который весь день не отрывает рук от клавиатуры.
 *
 * Поиск включается снаружи и только там, где выбирать приходится из длинного
 * списка: у агентства брендов бывает полтора десятка, и пролистывание их
 * стрелками — не выбор, а перебор.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { t } from '../i18n';

export interface MenuItem {
  key: string;
  /** Основная строка. */
  label: string;
  /** Вторая строка мелким: роль, число брендов, назначение пространства. */
  note?: string;
  /** Что ещё считается совпадением при поиске: домен, имя клиента. */
  search?: string;
  selected?: boolean;
  onSelect: () => void;
}

export interface MenuGroup {
  key: string;
  /** Заголовок нужен, только когда групп больше одной. */
  title?: string;
  items: MenuItem[];
  /** Показывается вместо списка, когда в группе нечего выбирать. */
  empty?: string;
}

/** Совпадение ищется по всему, что видно в строке, и по скрытым приметам. */
function matches(item: MenuItem, needle: string): boolean {
  return `${item.label} ${item.note ?? ''} ${item.search ?? ''}`
    .toLocaleLowerCase('ru')
    .includes(needle);
}

export function Menu({
  label,
  trigger,
  groups,
  align = 'start',
  search,
}: {
  /** Название меню для тех, кто его не видит. */
  label: string;
  trigger: ReactNode;
  groups: MenuGroup[];
  align?: 'start' | 'end';
  /** Подсказка в поле поиска. Нет подсказки — нет и поля. */
  search?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const listId = useId();
  // Идентификаторы опций нужны только в режиме поиска: на них показывает
  // `aria-activedescendant`. Ключ пункта уже уникален в пределах меню.
  const optionId = (key: string) => `${listId}-${key}`;
  const groupId = (key: string) => `${listId}-g-${key}`;

  const needle = query.trim().toLocaleLowerCase('ru');
  const shown = needle
    ? groups
        .map((group) => ({
          ...group,
          items: group.items.filter((i) => matches(i, needle)),
        }))
        .filter((group) => group.items.length > 0)
    : groups;
  const flat = shown.flatMap((group) => group.items);

  const close = useCallback((focusTrigger = true) => {
    setOpen(false);
    setQuery('');
    if (focusTrigger) button.current?.focus();
  }, []);

  // Клик мимо и Escape. Слушатели живут только пока меню открыто: висящий
  // обработчик на document — самый дешёвый способ получить утечку.
  useEffect(() => {
    if (!open) return;

    const onPointer = (event: PointerEvent) => {
      // Гасим и набранный запрос: список, оставшийся отфильтрованным с прошлого
      // раза, при следующем открытии выглядит как «половина брендов пропала».
      // Фокус за собой не тянем — человек уже щёлкнул мимо, он в другом месте.
      if (!root.current?.contains(event.target as Node)) close(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Поиск получает фокус сразу: открыть список и начать печатать — одно
  // движение, а не два.
  useEffect(() => {
    if (open && search) field.current?.focus();
  }, [open, search]);

  const openAt = (index: number) => {
    setActive(index);
    setOpen(true);
  };

  function onTriggerKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAt(
        Math.max(
          flat.findIndex((item) => item.selected),
          0,
        ),
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(Math.max(flat.length - 1, 0));
    }
  }

  function onListKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Tab') {
      // Tab уводит из меню — это выход, а не выбор. Фокус за собой не тянем, он
      // и так идёт дальше, но набранный запрос гасим: иначе следующее открытие
      // покажет список, отфильтрованный прошлым поиском, и половина брендов в
      // нём «пропадёт».
      close(false);
      return;
    }
    if (flat.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(flat.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      flat[active]?.onSelect();
      close();
    } else if (event.key === ' ' && !search) {
      // В поле поиска пробел — это пробел, а не выбор строки.
      event.preventDefault();
      flat[active]?.onSelect();
      close();
    }
  }

  // Место пункта в общем порядке обхода. Считается один раз списком, а не
  // счётчиком по ходу отрисовки: ключ пункта уникален в пределах меню, а
  // переменная, меняющаяся во время рендера, — это разное поведение на
  // повторных отрисовках.
  const position = new Map(flat.map((item, at) => [item.key, at]));

  return (
    <div className="menu" ref={root}>
      <button
        ref={button}
        type="button"
        className="context"
        aria-haspopup={search ? undefined : 'menu'}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label}
        onClick={() => (open ? close(false) : openAt(0))}
        onKeyDown={onTriggerKey}
      >
        {trigger}
      </button>

      {open ? (
        <div
          id={search ? undefined : listId}
          className={`menu-list${align === 'end' ? ' to-end' : ''}`}
          role={search ? undefined : 'menu'}
          aria-label={search ? undefined : label}
          tabIndex={search ? undefined : -1}
          ref={(node) => {
            if (!search) node?.focus();
          }}
          onKeyDown={onListKey}
        >
          {/* С поиском это уже не меню, а поле со списком, и роли обязаны это
              сказать. Пункты в `role="menu"` рядом с полем ввода читателю с
              экрана не объявляются, а подсветка стрелками остаётся чисто
              зрительной: без `aria-activedescendant` он слышит только то, что
              набрал. Поэтому здесь настоящий combobox — поле, список опций и
              активный потомок, названный по имени. */}
          {search ? (
            <input
              ref={field}
              className="menu-search"
              type="search"
              role="combobox"
              value={query}
              placeholder={search}
              aria-label={search}
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                flat[active] ? optionId(flat[active].key) : undefined
              }
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
            />
          ) : null}

          <div
            id={search ? listId : undefined}
            role={search ? 'listbox' : undefined}
            aria-label={search ? label : undefined}
          >
            {shown.map((group) => (
              <div className="menu-group" key={group.key}>
                {group.title ? (
                  <h3 id={search ? groupId(group.key) : undefined}>{group.title}</h3>
                ) : null}

                {group.items.length === 0 ? (
                  <p className="menu-empty">{group.empty ?? t('menu.empty')}</p>
                ) : (
                  <div
                    role={search ? 'group' : undefined}
                    aria-labelledby={
                      search && group.title ? groupId(group.key) : undefined
                    }
                  >
                    {group.items.map((item) => {
                      const here = position.get(item.key) ?? 0;
                      return (
                        <button
                          key={item.key}
                          id={search ? optionId(item.key) : undefined}
                          type="button"
                          role={search ? 'option' : 'menuitemradio'}
                          aria-selected={search ? Boolean(item.selected) : undefined}
                          aria-checked={search ? undefined : Boolean(item.selected)}
                          // Фокус остаётся в поле поиска: щелчок по строке не
                          // должен уводить его туда, откуда стрелки уже не
                          // работают.
                          tabIndex={-1}
                          className={`menu-item${here === active ? ' active' : ''}`}
                          onMouseEnter={() => setActive(here)}
                          onClick={() => {
                            item.onSelect();
                            close();
                          }}
                        >
                          <span className="menu-label">{item.label}</span>
                          {item.note ? (
                            <span className="menu-note">{item.note}</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {needle && flat.length === 0 ? (
            <p className="menu-empty">{t('menu.no-results', { query: query.trim() })}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
